import React, { useState, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Shipping from '@mui/icons-material/LocalShippingOutlined';
import Popover from '@mui/material/Popover';
import Tooltip from '@mui/material/Tooltip';
import { useBillingHeaderProvider } from 'mf_core/provider';
import { BillingModeSelectorContent } from './BillingModeSelectorContent';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import { BillingHeaderContextTo } from 'mf_core/provider/billing-header/billing-header-context';
import {
  businessCheckIcon,
  businessIcon,
  CustomerCheckIcon,
  CustomerIcon,
} from './BillingModeSelectorIcons';
import Stack from '@mui/material/Stack';

type Props = {
  resellerData: any;
};

export function BillingModeSelector(Props: Props) {
  const { resellerData } = Props;
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [isMyBusiness, setIsMyBusiness] = useState(true);
  const [selectedAddress, setSelectedAddress] = useState<any>({});
  const { modifyBillingHeader } = useBillingHeaderProvider();
  const [defaultAddress, setDefaultAddress] = useState<any>({});
  const [selectedTab, setSelectedTab] = useState<'business' | 'customer'>(
    'business'
  );

  const getFormattedAddress = (address: any) => {
    let FormatAddress = [
      address?.line1,
      address?.line2,
      address?.line3,
      address?.line4,
      address?.postalCode,
      address?.city,
    ];
    FormatAddress = FormatAddress?.map((x) => (x ? x?.trimEnd() : x))?.filter(
      (x) => x !== '' && x !== null
    );
    return FormatAddress.join(',');
  };
  useEffect(() => {
    if (resellerData) {
      const billaddress = resellerData.billToAddresses.filter(
        (x: any) => x.imAddressId === '000'
      );
      setDefaultAddress(billaddress[0]);
      setSelectedAddress(billaddress?.[0]);
      if (billaddress) {
        const newObj: BillingHeaderContextTo = {
          billingAddress: billaddress,
          cnpjNumber: billaddress[0].taxExemptNumber,
          hasIE: resellerData.hasIE,
          resellerId: resellerData.id,
          resellerType: resellerData.resellerType,
          shipToProvince: billaddress[0].city,
          shipToUF: billaddress[0].state,
        };
        modifyBillingHeader(newObj);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resellerData]);

  const primaryOnClick = (myBusiness: boolean, selectedAddress: any) => {
    setPopoverOpen(false);
    setSelectedTab(myBusiness ? 'business' : 'customer');
    setSelectedAddress(selectedAddress);

    const newObj: BillingHeaderContextTo = {
      billingAddress: [selectedAddress],
      cnpjNumber: selectedAddress?.taxExemptNumber,
      shipToProvince: selectedAddress?.city,
      shipToUF: selectedAddress?.state,
    };
    modifyBillingHeader(newObj);
  };

  const tootipTitle = () => {
    return (
      <Stack spacing={1}>
        <Typography variant="text-xs-semibold">{`${
          selectedTab === 'customer' ? 'Customer' : 'Reseller'
        }:${selectedAddress?.taxExemptNumber ?? ''}`}</Typography>
        <Typography variant="text-xs-medium">
          {getFormattedAddress(selectedAddress)}
        </Typography>
      </Stack>
    );
  };

  return (
    <Box margin="0 15px" width="10rem">
      <Tooltip title={tootipTitle()}>
        <StyledButton
          data-name={'customer'}
          disableRipple={true}
          onClick={() => setPopoverOpen(true)}
          startIcon={<Shipping color="primary" />}
        >
          <TypographySelector variant="smalltextsemibold">
            {`${selectedTab === 'customer' ? 'Customer' : 'Reseller'}: ${
              selectedAddress?.taxExemptNumber ?? ''
            }`}
          </TypographySelector>
        </StyledButton>
      </Tooltip>

      <StylePopover open={popoverOpen}>
        <Box
          key="header"
          sx={{
            alignItems: 'flex-start',
            alignSelf: 'stretch',
            display: 'flex',
            flexDirection: 'column',
            height: '156px',
            padding: '20px 16px 8px 16px',
            width: '360px',
          }}
        >
          <TableRow
            sx={{
              alignItems: 'center',
              alignSelf: 'stretch',
              display: 'flex',
              gap: '16px',
              height: '128px',
              width: '328px',
            }}
          >
            <HeaderTableCell
              key="myBusiness"
              style={{
                background: isMyBusiness ? Primary50 : Gray25,
                border: isMyBusiness ? BorderPrimary700 : BorderGray200,
              }}
            >
              <IconButton
                onClick={() => {
                  setIsMyBusiness(true);
                }}
              >
                {isMyBusiness ? businessCheckIcon : businessIcon}
              </IconButton>
              My business
            </HeaderTableCell>
            <HeaderTableCell
              key="MyCustomer"
              style={{
                background: !isMyBusiness ? Primary50 : Gray25,
                border: !isMyBusiness ? BorderPrimary700 : BorderGray200,
              }}
            >
              <IconButton
                onClick={() => {
                  setIsMyBusiness(false);
                }}
              >
                {!isMyBusiness ? CustomerCheckIcon : CustomerIcon}
              </IconButton>
              My customer
            </HeaderTableCell>
          </TableRow>
        </Box>
        <Box>
          <BillingModeSelectorContent
            defaultAddress={defaultAddress}
            getFormattedAddress={getFormattedAddress}
            headerSelectedAddress={selectedAddress}
            isMyBusiness={isMyBusiness}
            onPrimaryClick={primaryOnClick}
            selectedTab={selectedTab}
            setPopoverOpen={setPopoverOpen}
            shipToAddresses={resellerData?.billToAddresses}
          />
        </Box>
      </StylePopover>
    </Box>
  );
}

const StyledButton = styled(Button)(({ theme }) => ({
  '&:focus': {
    outline: 'none',
  },
  '&:hover': {
    backgroundColor: '#ebebeb',
  },
  alignItems: 'flex-start',
  color: theme.palette.info.main,
  display: 'flex',
  fontSize: '14px',
  fontWeight: 600,
  height: '32px',
  lineHeight: '22px',
  marginLeft: '30px',
  padding: '5px 12px',
  right: '32px',
  textTransform: 'none',
}));

const StylePopover = styled(Popover)(() => ({
  '& .MuiPaper-root': {
    boxShadow: 'none',
  },
  '& .MuiPopover-paper': {
    border: '1px solid var(--Gray-200, #E3E8EF)',
    borderRadius: 8,
    boxShadow:
      '0px 12px 16px -4px rgba(18, 25, 38, 0.08), 0px 4px 6px -2px rgba(18, 25, 38, 0.03)',
    left: '206px !important',
    maxHeight: '600px',
    minHeight: '420px',
    overflow: 'hidden',
    right: '18px !important',
    top: '42px !important',
    width: '360px',
  },
}));

const TypographySelector = styled(Typography)(() => ({
  fontFamily: 'Roboto',
  fontSize: '14px',
  fontStyle: 'normal',
  fontWeight: '600',
  lineHeight: '22px',
}));
const HeaderTableCell = styled(TableCell)(() => ({
  alignItems: 'center',
  borderRadius: 'var(--radius-md, 8px)',
  boxShadow:
    'var(--shadow-sm-1-x, 0px) var(--shadow-sm-1-y, 1px) var(--shadow-sm-1-blur, 3px) var(--shadow-sm-1-spread, 0px) rgba(18, 25, 38, 0.10), var(--shadow-sm-2-x, 0px) var(--shadow-sm-2-y, 1px) var(--shadow-sm-2-blur, 2px) var(--shadow-sm-2-spread, 0px) rgba(18, 25, 38, 0.06)',
  display: 'flex',
  flex: '1 0 0',
  flexDirection: 'column',
  gap: '4px',
  justifyContent: 'center',
  padding: '20px 14px 8px 14px',
}));

const IconButton = styled(Button)(({ theme }) => ({
  '&:hover': {
    background: 'inherit',
  },
  alignItems: 'center',
  border: '0px',
  display: 'flex',
  gap: '8px',
  height: '60px',
  justifyContent: 'center',
  padding: '8px',
  width: '60px',
}));

const Primary50 = 'var(--color-primary-50, #EFF8FF)';
const Gray25 = 'var(--color-gray-25, #FCFCFD)';
const BorderPrimary700 = '2px solid var(--color-primary-700, #175CD3)';
const BorderGray200 = '1px solid var(--color-gray-200, #D4DCE7)';
